/**
 * Role taxonomy — must match REQUIREMENTS.md §5.
 * Frontend never trusts client-side role checks for security; this drives
 * NAVIGATION VISIBILITY only. Backend enforces authorization on every route.
 */

export type AppRole =
  | "pending"
  | "Agent"
  | "Supervisor"
  | "WorkforceAgent"
  | "OperationsManager"
  | "Executive"
  | "Administrator";

export const ROLE_LABELS: Record<AppRole, string> = {
  pending: "Pending approval",
  Agent: "Agent",
  Supervisor: "Supervisor",
  WorkforceAgent: "Workforce",
  OperationsManager: "Operations Manager",
  Executive: "Executive",
  Administrator: "Administrator",
};

/** Sorted from least-privileged to most. Used by the Users admin dropdown. */
export const ASSIGNABLE_ROLES: AppRole[] = [
  "Agent",
  "Supervisor",
  "WorkforceAgent",
  "OperationsManager",
  "Executive",
  "Administrator",
];

/**
 * Permission matrix for navigation visibility (NOT a security boundary).
 *
 * Each capability lists the roles allowed to see the corresponding nav entry.
 * Add a new capability here when you add a new top-level section.
 */
export const NAV_CAPABILITIES = {
  softphone:   ["Agent", "Supervisor", "OperationsManager", "Administrator"] as AppRole[],
  voicemails:  ["Agent", "Supervisor", "OperationsManager", "Administrator"] as AppRole[],
  liveQueues:  ["Supervisor", "OperationsManager", "Executive", "Administrator"] as AppRole[],
  routing:     ["OperationsManager", "Administrator"] as AppRole[],
  scheduling:  ["WorkforceAgent", "OperationsManager", "Administrator"] as AppRole[],
  reports:     ["Agent", "Supervisor", "WorkforceAgent", "OperationsManager", "Executive", "Administrator"] as AppRole[],
  activity:    ["OperationsManager", "Executive", "Administrator"] as AppRole[],
  adminUsers:  ["OperationsManager", "Administrator"] as AppRole[],
  adminTenant: ["Administrator"] as AppRole[],
};

export function can(role: AppRole | undefined, capability: keyof typeof NAV_CAPABILITIES): boolean {
  if (!role) return false;
  return NAV_CAPABILITIES[capability].includes(role);
}
